export interface WorkExperienceSortInput {
  start_date?: string | null;
  end_date?: string | null;
  is_current?: boolean | null;
  duration?: string | null;
}

export interface EducationSortInput {
  start_date?: string | null;
  end_date?: string | null;
  graduation_date?: string | null;
  completion_date?: string | null;
  is_current?: boolean | null;
  is_ongoing?: boolean | null;
  status?: string | null;
  year?: string | null;
}

export interface CertificationSortInput {
  issue_date?: string | null;
  issued_at?: string | null;
  completion_date?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
  year?: string | null;
}

const PRESENT_DATE_PATTERN = /^(present|current|now|ongoing)$/i;
const MONTH_YEAR_PATTERN = /^(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\s+(\d{4})$/i;
const MONTH_LOOKUP: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

function splitDateRange(value: string | null | undefined): readonly [string | null, string | null] {
  const normalized = value?.trim();
  if (!normalized) return [null, null];
  const parts = normalized.split(/\s+(?:\u2014|\u2013|-|to)\s+/i);
  if (parts.length < 2) return [normalized, null];
  return [parts[0]?.trim() || null, parts.slice(1).join(' - ').trim() || null];
}

export function parseTimelineDate(value: string | number | Date | null | undefined): number | null {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = value?.trim();
  if (!normalized || PRESENT_DATE_PATTERN.test(normalized)) return null;

  const yearOnlyMatch = normalized.match(/^(\d{4})$/);
  if (yearOnlyMatch) {
    const year = Number(yearOnlyMatch[1]);
    return Number.isFinite(year) ? Date.UTC(year, 0, 1) : null;
  }

  const monthYearMatch = normalized.match(MONTH_YEAR_PATTERN);
  if (monthYearMatch) {
    const month = MONTH_LOOKUP[monthYearMatch[1].toLowerCase()];
    const year = Number(monthYearMatch[2]);
    return Number.isFinite(year) ? Date.UTC(year, month, 1) : null;
  }

  const isoMonthMatch = normalized.match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);
  if (isoMonthMatch) {
    const year = Number(isoMonthMatch[1]);
    const month = Number(isoMonthMatch[2]) - 1;
    if (Number.isFinite(year) && month >= 0 && month <= 11) {
      return Date.UTC(year, month, 1);
    }
  }

  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

export function sortByTimelineDesc<T>(
  items: readonly T[],
  getDateCandidates: (item: T) => readonly (string | number | Date | null | undefined)[],
): T[] {
  return items
    .map((item, index) => {
      const timestamp = getDateCandidates(item)
        .map(parseTimelineDate)
        .find((time): time is number => time !== null) ?? null;
      return { item, index, timestamp };
    })
    .sort((a, b) => {
      if (a.timestamp === null && b.timestamp === null) return a.index - b.index;
      if (a.timestamp === null) return 1;
      if (b.timestamp === null) return -1;
      if (a.timestamp !== b.timestamp) return b.timestamp - a.timestamp;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

function getExperienceSortMeta(exp: WorkExperienceSortInput) {
  const [rangeStart, rangeEnd] = splitDateRange(exp.duration ?? exp.start_date);
  const startValue = rangeEnd ? rangeStart : exp.start_date;
  const endValue = exp.end_date ?? rangeEnd;
  const explicitlyCurrent =
    exp.is_current === true ||
    PRESENT_DATE_PATTERN.test(endValue?.trim() ?? '');

  return {
    explicitlyCurrent,
    endTime: explicitlyCurrent ? Number.POSITIVE_INFINITY : parseTimelineDate(endValue),
    startTime: parseTimelineDate(startValue),
  };
}

export function sortWorkExperiencesNewestFirst<T extends WorkExperienceSortInput>(
  experiences: readonly T[],
): T[] {
  return experiences
    .map((experience, index) => ({
      experience,
      index,
      meta: getExperienceSortMeta(experience),
    }))
    .sort((a, b) => {
      if (a.meta.explicitlyCurrent !== b.meta.explicitlyCurrent) {
        return a.meta.explicitlyCurrent ? -1 : 1;
      }

      const aEnd = a.meta.endTime ?? Number.NEGATIVE_INFINITY;
      const bEnd = b.meta.endTime ?? Number.NEGATIVE_INFINITY;
      if (aEnd !== bEnd) return bEnd - aEnd;

      const aStart = a.meta.startTime ?? Number.NEGATIVE_INFINITY;
      const bStart = b.meta.startTime ?? Number.NEGATIVE_INFINITY;
      if (aStart !== bStart) return bStart - aStart;

      return a.index - b.index;
    })
    .map((item) => item.experience);
}

export function sortEducationNewestFirst<T extends EducationSortInput>(
  education: readonly T[],
): T[] {
  return education
    .map((item, index) => {
      const current =
        item.is_current === true ||
        item.is_ongoing === true ||
        PRESENT_DATE_PATTERN.test(item.status?.trim() ?? '') ||
        PRESENT_DATE_PATTERN.test(item.end_date?.trim() ?? '') ||
        PRESENT_DATE_PATTERN.test(item.graduation_date?.trim() ?? '');
      const timestamp = current
        ? Number.POSITIVE_INFINITY
        : [item.graduation_date, item.completion_date, item.end_date, item.year, item.start_date]
            .map(parseTimelineDate)
            .find((time): time is number => time !== null) ?? null;
      return { item, index, current, timestamp };
    })
    .sort((a, b) => {
      if (a.current !== b.current) return a.current ? -1 : 1;
      if (a.timestamp === null && b.timestamp === null) return a.index - b.index;
      if (a.timestamp === null) return 1;
      if (b.timestamp === null) return -1;
      if (a.timestamp !== b.timestamp) return b.timestamp - a.timestamp;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

export function sortCertificationsNewestFirst<T extends CertificationSortInput>(
  certifications: readonly T[],
): T[] {
  return sortByTimelineDesc(certifications, (cert) => [
    cert.issue_date,
    cert.issued_at,
    cert.completion_date,
    cert.completed_at,
    cert.year,
    cert.created_at,
  ]);
}
